<template>
  <div class="dre-root">

    <!-- ═══ SECTION 1: PAYLOAD FIELDS ═══════════════════════════════ -->
    <div class="dre-section">
      <div class="dre-section-head">
        <div class="dre-section-icon indigo">
          <v-icon small color="white">mdi-variable</v-icon>
        </div>
        <div>
          <div class="dre-section-title">Payload Fields</div>
          <div class="dre-section-sub">Maps to template placeholders like <code>{name}</code>, <code>{music}</code>, etc.</div>
        </div>
      </div>

      <div class="dre-fields-list">
        <transition-group name="field-fade">
          <div v-for="(field, index) in payloadFields" :key="field._uid" class="dre-field-card">
            <!-- Card header: key + type chip + remove -->
            <div class="dre-field-header">
              <v-combobox
                v-model="field.key"
                :items="availablePayloadKeys"
                dense
                solo
                flat
                hide-details
                class="dre-key-box"
                placeholder="field key"
              >
                <template #prepend-inner>
                  <v-icon x-small class="mr-1" color="grey darken-1">mdi-pound</v-icon>
                </template>
              </v-combobox>

              <!-- Type chips -->
              <div class="dre-type-chips">
                <v-tooltip bottom v-for="t in expressionTypes" :key="t.value">
                  <template #activator="{ on }">
                    <v-chip
                      x-small
                      :color="field.type === t.value ? typeColor(t.value) : 'grey lighten-3'"
                      :text-color="field.type === t.value ? 'white' : 'grey darken-1'"
                      class="dre-type-chip"
                      v-on="on"
                      @click="setFieldType(field, t.value)"
                    >
                      <v-icon x-small left>{{ typeIcon(t.value) }}</v-icon>
                      {{ t.label }}
                    </v-chip>
                  </template>
                  <span>{{ t.text }}</span>
                </v-tooltip>
              </div>

              <v-btn icon x-small color="red lighten-2" class="ml-auto" @click="removePayloadField(index)">
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>

            <!-- Card body: value inputs -->
            <div class="dre-field-body">
              <!-- data type -->
              <template v-if="field.type === 'data'">
                <v-text-field
                  v-model="field.dataField"
                  dense outlined hide-details
                  placeholder="Request field name (e.g. user_id, comment)"
                  class="dre-value-input"
                >
                  <template #prepend-inner>
                    <v-icon x-small color="indigo" class="mr-1">mdi-database-arrow-right</v-icon>
                  </template>
                </v-text-field>
              </template>

              <!-- esb type -->
              <template v-else-if="field.type === 'esb'">
                <div class="dre-esb-row">
                  <v-combobox
                    v-model="field.esbAlias"
                    :items="esbAliasOptions"
                    item-text="text"
                    item-value="value"
                    dense outlined hide-details
                    placeholder="ESB script (e.g. current-user)"
                    class="dre-esb-alias"
                  >
                    <template #prepend-inner>
                      <v-icon x-small color="purple" class="mr-1">mdi-lightning-bolt</v-icon>
                    </template>
                  </v-combobox>
                  <div class="dre-dot-sep">.</div>
                  <v-text-field
                    v-model="field.esbProp"
                    dense outlined hide-details
                    placeholder="property (e.g. fullname)"
                    class="dre-esb-prop"
                  />
                </div>
              </template>

              <!-- result type -->
              <template v-else-if="field.type === 'result'">
                <v-text-field
                  v-model="field.resultField"
                  dense outlined hide-details
                  placeholder="Result field name (e.g. id, title)"
                  class="dre-value-input"
                >
                  <template #prepend-inner>
                    <v-icon x-small color="teal" class="mr-1">mdi-arrow-u-left-top</v-icon>
                  </template>
                </v-text-field>
              </template>

              <!-- literal type -->
              <template v-else-if="field.type === 'literal'">
                <v-text-field
                  v-model="field.literalValue"
                  dense outlined hide-details
                  placeholder="Static value (e.g. https://example.com/img.png)"
                  class="dre-value-input"
                >
                  <template #prepend-inner>
                    <v-icon x-small color="grey darken-1" class="mr-1">mdi-format-text</v-icon>
                  </template>
                </v-text-field>
              </template>

              <!-- Filter chip row -->
              <div v-if="['data','esb'].includes(field.type)" class="dre-filter-row">
                <span class="dre-filter-label">
                  <v-icon x-small class="mr-1" color="grey">mdi-filter</v-icon>Filter:
                </span>
                <v-chip
                  v-for="f in filterOptions"
                  :key="f.value"
                  x-small
                  :outlined="field.filter !== f.value"
                  :color="field.filter === f.value ? 'orange darken-1' : 'grey lighten-2'"
                  :text-color="field.filter === f.value ? 'white' : 'grey darken-2'"
                  class="dre-filter-chip"
                  @click="field.filter = field.filter === f.value ? '' : f.value"
                >
                  <v-icon x-small left v-if="field.filter === f.value">mdi-check</v-icon>
                  {{ f.label }}
                </v-chip>
              </div>

              <!-- Expression preview -->
              <div class="dre-expr-preview" v-if="buildExpression(field)">
                <v-icon x-small class="mr-1" color="grey">mdi-eye-outline</v-icon>
                <code class="expr-code">{{ buildExpression(field) }}</code>
              </div>
            </div>
          </div>
        </transition-group>
      </div>

      <v-btn small text color="indigo" class="mt-2" @click="addPayloadField">
        <v-icon small left>mdi-plus-circle</v-icon>Add Field
      </v-btn>
    </div>

    <div class="dre-divider" />

    <!-- ═══ SECTION 2: URI ══════════════════════════════════════════ -->
    <div class="dre-section">
      <div class="dre-section-head">
        <div class="dre-section-icon teal">
          <v-icon small color="white">mdi-link-variant</v-icon>
        </div>
        <div>
          <div class="dre-section-title">URI / Path</div>
          <div class="dre-section-sub">Use <code>[data:field]</code>, <code>[esb:alias.prop]</code>, or <code>[result:field]</code> for dynamic segments</div>
        </div>
      </div>

      <v-text-field
        v-model="uriValue"
        dense outlined hide-details
        placeholder="/music/[result:id]"
        class="mt-2"
      >
        <template #prepend-inner>
          <v-icon x-small color="teal" class="mr-1">mdi-slash-forward</v-icon>
        </template>
      </v-text-field>

      <div v-if="uriTokens.length > 0" class="dre-token-row mt-2">
        <span class="dre-filter-label">
          <v-icon x-small class="mr-1" color="grey">mdi-code-braces</v-icon>Tokens:
        </span>
        <v-chip
          v-for="token in uriTokens"
          :key="token"
          x-small
          color="teal lighten-5"
          text-color="teal darken-2"
          outlined
          class="ml-1"
        >
          <v-icon x-small left>mdi-flash</v-icon>{{ token }}
        </v-chip>
      </div>
    </div>

    <div class="dre-divider" />

    <!-- ═══ SECTION 3: TARGET ══════════════════════════════════════ -->
    <div class="dre-section">
      <div class="dre-section-head">
        <div class="dre-section-icon orange">
          <v-icon small color="white">mdi-target-account</v-icon>
        </div>
        <div>
          <div class="dre-section-title">Notification Target</div>
          <div class="dre-section-sub">Who receives the push notification</div>
        </div>
      </div>

      <!-- Topic chips -->
      <div class="dre-topic-chips mt-2">
        <v-chip
          v-for="t in topicOptions"
          :key="t.value"
          small
          :color="target.topic === t.value ? topicColor(t.value) : 'grey lighten-3'"
          :text-color="target.topic === t.value ? 'white' : 'grey darken-2'"
          class="dre-topic-chip"
          @click="target.topic = t.value"
        >
          <v-icon small left>{{ t.icon }}</v-icon>
          {{ t.text }}
        </v-chip>
      </div>

      <!-- Topic hint -->
      <div class="dre-topic-hint mt-2" :class="`hint-${target.topic}`">
        <v-icon x-small class="mr-1">{{ topicMeta.icon }}</v-icon>
        {{ topicMeta.hint }}
      </div>

      <!-- Value expression (non-public) -->
      <template v-if="target.topic !== 'public'">
        <div class="dre-target-value-card mt-3">
          <div class="dre-field-header">
            <v-icon x-small class="mr-1" color="grey darken-1">mdi-equal</v-icon>
            <span class="dre-filter-label">Value Expression</span>
            <!-- Type chips -->
            <div class="dre-type-chips ml-2">
              <v-chip
                v-for="t in expressionTypes"
                :key="t.value"
                x-small
                :color="target.valueType === t.value ? typeColor(t.value) : 'grey lighten-3'"
                :text-color="target.valueType === t.value ? 'white' : 'grey darken-1'"
                class="dre-type-chip"
                @click="setTargetType(t.value)"
              >
                <v-icon x-small left>{{ typeIcon(t.value) }}</v-icon>
                {{ t.label }}
              </v-chip>
            </div>
          </div>

          <div class="dre-field-body mt-2">
            <v-text-field
              v-if="target.valueType === 'data'"
              v-model="target.dataField"
              dense outlined hide-details
              placeholder="Request field (e.g. user_id)"
            >
              <template #prepend-inner>
                <v-icon x-small color="indigo" class="mr-1">mdi-database-arrow-right</v-icon>
              </template>
            </v-text-field>

            <div v-else-if="target.valueType === 'esb'" class="dre-esb-row">
              <v-combobox
                v-model="target.esbAlias"
                :items="esbAliasOptions"
                item-text="text"
                item-value="value"
                dense outlined hide-details
                placeholder="ESB script"
                class="dre-esb-alias"
              >
                <template #prepend-inner>
                  <v-icon x-small color="purple" class="mr-1">mdi-lightning-bolt</v-icon>
                </template>
              </v-combobox>
              <div class="dre-dot-sep">.</div>
              <v-text-field
                v-model="target.esbProp"
                dense outlined hide-details
                placeholder="property (e.g. id)"
                class="dre-esb-prop"
              />
            </div>

            <v-text-field
              v-else-if="target.valueType === 'result'"
              v-model="target.resultField"
              dense outlined hide-details
              placeholder="Result field (e.g. id)"
            >
              <template #prepend-inner>
                <v-icon x-small color="teal" class="mr-1">mdi-arrow-u-left-top</v-icon>
              </template>
            </v-text-field>
          </div>
        </div>
      </template>
    </div>

    <div class="dre-divider" />

    <!-- ═══ SECTION 4: PARAMETERS ═════════════════════════════════ -->
    <div class="dre-section">
      <div class="dre-section-head">
        <div class="dre-section-icon green">
          <v-icon small color="white">mdi-playlist-plus</v-icon>
        </div>
        <div>
          <div class="dre-section-title">Parameters <span class="optional-badge">Optional</span></div>
          <div class="dre-section-sub">Action buttons shown in the notification</div>
        </div>
      </div>

      <div v-for="(param, index) in parameters" :key="index" class="dre-param-card mt-2">
        <div class="dre-param-header">
          <v-chip x-small :color="paramTypeColor(param.type)" text-color="white" class="mr-2">
            <v-icon x-small left>{{ paramTypeIcon(param.type) }}</v-icon>
            {{ param.type || 'button' }}
          </v-chip>
          <span class="dre-param-label">{{ param.label || 'Untitled' }}</span>
          <v-btn icon x-small color="red lighten-2" class="ml-auto" @click="removeParameter(index)">
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="dre-param-body">
          <v-row dense>
            <v-col cols="3">
              <v-select
                v-model="param.type"
                :items="paramTypeOptions"
                dense outlined hide-details
                label="Type"
              />
            </v-col>
            <v-col cols="4">
              <v-text-field v-model="param.label" dense outlined hide-details placeholder="Label (e.g. Follow Back)">
                <template #prepend-inner><v-icon x-small color="grey" class="mr-1">mdi-tag</v-icon></template>
              </v-text-field>
            </v-col>
            <v-col cols="5">
              <v-text-field v-model="param.path" dense outlined hide-details placeholder="Path (e.g. /follow/[data:user_id])">
                <template #prepend-inner><v-icon x-small color="grey" class="mr-1">mdi-link</v-icon></template>
              </v-text-field>
            </v-col>
            <v-col cols="12" v-if="param.type !== 'button'">
              <v-text-field v-model="param.show" dense outlined hide-details placeholder="Show condition (optional)">
                <template #prepend-inner><v-icon x-small color="grey" class="mr-1">mdi-eye-check</v-icon></template>
              </v-text-field>
            </v-col>
          </v-row>
        </div>
      </div>

      <v-btn small text color="green darken-1" class="mt-2" @click="addParameter">
        <v-icon small left>mdi-plus-circle</v-icon>Add Parameter
      </v-btn>
    </div>

    <div class="dre-divider" />

    <!-- ═══ JSON PREVIEW ══════════════════════════════════════════ -->
    <div class="dre-section">
      <div class="dre-section-head">
        <div class="dre-section-icon grey">
          <v-icon small color="white">mdi-code-json</v-icon>
        </div>
        <div style="flex:1">
          <div class="dre-section-title">Live JSON Preview</div>
        </div>
        <v-btn x-small text color="grey darken-1" @click="copyJson" class="ml-auto">
          <v-icon x-small left>mdi-content-copy</v-icon>Copy
        </v-btn>
      </div>
      <div class="dre-json-wrap mt-2">
        <pre class="dre-json">{{ jsonPreview }}</pre>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'DataReplacementEditor',

  props: {
    value: { type: String, default: '' }
  },

  data() {
    return {
      payloadFields: [],
      uriValue: '',
      target: {
        topic: 'user',
        valueType: 'data',
        dataField: '',
        esbAlias: '',
        esbProp: '',
        resultField: ''
      },
      parameters: [],
      _uidCounter: 0,

      availablePayloadKeys: [
        'icon','name','artistName','music','comment',
        'message','label','media','thumb','title','body','url'
      ],

      expressionTypes: [
        { value: 'data',    label: 'data',    text: 'data: — Read from request payload field',   icon: 'mdi-database-arrow-right' },
        { value: 'esb',     label: 'esb',     text: 'esb: — Run a Logic Control (ESB) script',  icon: 'mdi-lightning-bolt' },
        { value: 'result',  label: 'result',  text: 'result: — Read from service result field',  icon: 'mdi-arrow-u-left-top' },
        { value: 'literal', label: 'literal', text: 'literal — Static string value',             icon: 'mdi-format-text' }
      ],

      filterOptions: [
        { value: 'userFullname',            label: 'userFullname' },
        { value: 'base64Encode',            label: 'base64Encode' },
        { value: 'getNameFromEmailAddress', label: 'emailName' }
      ],

      topicOptions: [
        { value: 'public', text: 'Public',  icon: 'mdi-earth' },
        { value: 'user',   text: 'User',    icon: 'mdi-account' },
        { value: 'group',  text: 'Group',   icon: 'mdi-account-group' },
        { value: 'artist', text: 'Artist',  icon: 'mdi-music-note' }
      ],

      paramTypeOptions: ['button', 'link', 'action'],

      esbAliasOptions: [
        { value: 'current-user',              text: 'current-user — Authenticated user' },
        { value: 'get-current-user-avatar',   text: 'get-current-user-avatar — Avatar + fullname' },
        { value: 'user-avatar',               text: 'user-avatar — Specific user avatar' },
        { value: 'music-avatar',              text: 'music-avatar — Music item avatar' },
        { value: 'music-information-from-id', text: 'music-information-from-id — Title, artistName, id' },
        { value: 'user-music-from-rating',    text: 'user-music-from-rating — Music from rating' },
        { value: 'user-music-from-spotlight', text: 'user-music-from-spotlight — Music from spotlight' },
        { value: 'user-profile-information',  text: 'user-profile-information — Profile data' },
        { value: 'user-follower-lists',       text: 'user-follower-lists — Follower list' },
        { value: 'user-following-lists',      text: 'user-following-lists — Following list' },
        { value: 'community-lists',           text: 'community-lists — Community data' },
        { value: 'user-from-email',           text: 'user-from-email — User from email' },
        { value: 'userid-from-follower',      text: 'userid-from-follower — User ID from follower' },
        { value: 'leaderboard',               text: 'leaderboard — Leaderboard data' },
        { value: 'badge-metadata',            text: 'badge-metadata — Badge info' },
        { value: 'email-name-parse',          text: 'email-name-parse — Name from email' },
        { value: 'current-host',              text: 'current-host — Server host' }
      ]
    };
  },

  computed: {
    uriTokens() {
      return (this.uriValue.match(/\[[^\]]+\]/g) || []);
    },

    builtJson() {
      const obj = {};
      for (const field of this.payloadFields) {
        if (!field.key) continue;
        const expr = this.buildExpression(field);
        if (expr) obj[field.key] = expr;
      }
      if (this.uriValue) obj.uri = this.uriValue;
      const to = { topic: this.target.topic };
      if (this.target.topic !== 'public') {
        const expr = this.buildTargetExpression();
        if (expr) to.value = expr;
      }
      obj.to = to;
      if (this.parameters.length > 0) {
        obj.parameters = this.parameters.map(p => {
          const param = { type: p.type || 'button', label: p.label, path: p.path };
          if (p.show) param.show = p.show;
          return param;
        });
      }
      return obj;
    },

    jsonPreview() {
      return JSON.stringify(this.builtJson, null, 2);
    },

    topicMeta() {
      const map = {
        public: { icon: 'mdi-earth',         hint: 'All users with the app installed will receive this notification.' },
        user:   { icon: 'mdi-account',       hint: 'Only the specific user resolved from the value expression will receive this.' },
        group:  { icon: 'mdi-account-group', hint: 'All members of the resolved group will receive this.' },
        artist: { icon: 'mdi-music-note',    hint: 'All followers of the resolved artist will receive this.' }
      };
      return map[this.target.topic] || { icon: 'mdi-information', hint: '' };
    }
  },

  watch: {
    builtJson: {
      deep: true,
      handler(v) { this.$emit('input', JSON.stringify(v, null, 2)); }
    }
  },

  created() {
    this.loadFromJson(this.value);
  },

  methods: {
    newUid() { return ++this._uidCounter; },

    typeColor(t) {
      return { data: 'indigo', esb: 'purple', result: 'teal darken-1', literal: 'blue-grey' }[t] || 'grey';
    },
    typeIcon(t) {
      return { data: 'mdi-database-arrow-right', esb: 'mdi-lightning-bolt', result: 'mdi-arrow-u-left-top', literal: 'mdi-format-text' }[t] || 'mdi-help';
    },
    topicColor(t) {
      return { public: 'orange darken-1', user: 'indigo', group: 'green darken-1', artist: 'purple' }[t] || 'grey';
    },
    paramTypeColor(t) {
      return { button: 'blue darken-1', link: 'teal darken-1', action: 'orange darken-1' }[t] || 'grey';
    },
    paramTypeIcon(t) {
      return { button: 'mdi-gesture-tap-button', link: 'mdi-link', action: 'mdi-play-circle' }[t] || 'mdi-circle';
    },

    setFieldType(field, type) {
      field.type = type;
      field.dataField = ''; field.esbAlias = ''; field.esbProp = '';
      field.resultField = ''; field.literalValue = ''; field.filter = '';
    },
    setTargetType(type) {
      this.target.valueType = type;
      this.target.dataField = ''; this.target.esbAlias = ''; this.target.esbProp = ''; this.target.resultField = '';
    },

    buildExpression(field) {
      switch (field.type) {
        case 'data': {
          if (!field.dataField) return '';
          const base = `data:${field.dataField}`;
          return field.filter ? `${base}|${field.filter}` : base;
        }
        case 'esb': {
          const alias = typeof field.esbAlias === 'object' ? (field.esbAlias?.value || '') : (field.esbAlias || '');
          if (!alias) return '';
          const base = `esb:${alias}.${field.esbProp || ''}`;
          return field.filter ? `${base}|${field.filter}` : base;
        }
        case 'result':
          return field.resultField ? `result:${field.resultField}` : '';
        case 'literal':
          return field.literalValue || '';
        default: return '';
      }
    },

    buildTargetExpression() {
      const t = this.target;
      switch (t.valueType) {
        case 'data':   return t.dataField   ? `data:${t.dataField}` : '';
        case 'esb': {
          const alias = typeof t.esbAlias === 'object' ? (t.esbAlias?.value || '') : (t.esbAlias || '');
          return alias ? `esb:${alias}.${t.esbProp || ''}` : '';
        }
        case 'result': return t.resultField ? `result:${t.resultField}` : '';
        default: return '';
      }
    },

    parseExpression(expr) {
      const base = { type: 'data', dataField: '', esbAlias: '', esbProp: '', resultField: '', literalValue: '', filter: '' };
      if (!expr) return base;
      if (expr.startsWith('data:')) {
        const [df, filter] = expr.slice(5).split('|');
        return { ...base, type: 'data', dataField: df || '', filter: filter || '' };
      }
      if (expr.startsWith('esb:')) {
        const [ap, filter] = expr.slice(4).split('|');
        const dot = ap.indexOf('.');
        return { ...base, type: 'esb', esbAlias: dot >= 0 ? ap.slice(0, dot) : ap, esbProp: dot >= 0 ? ap.slice(dot + 1) : '', filter: filter || '' };
      }
      if (expr.startsWith('result:')) {
        return { ...base, type: 'result', resultField: expr.slice(7) };
      }
      return { ...base, type: 'literal', literalValue: expr };
    },

    loadFromJson(json) {
      if (!json) return;
      let p;
      try { p = JSON.parse(json); } catch { return; }
      this.payloadFields = [];
      for (const key of Object.keys(p)) {
        if (['uri','to','parameters'].includes(key)) continue;
        this.payloadFields.push({ _uid: this.newUid(), key, ...this.parseExpression(p[key]) });
      }
      if (p.uri) this.uriValue = p.uri;
      if (p.to) {
        this.target.topic = p.to.topic || 'user';
        if (p.to.value) {
          const parsed = this.parseExpression(p.to.value);
          this.target.valueType   = parsed.type;
          this.target.dataField   = parsed.dataField;
          this.target.esbAlias    = parsed.esbAlias;
          this.target.esbProp     = parsed.esbProp;
          this.target.resultField = parsed.resultField;
        }
      }
      if (p.parameters) {
        this.parameters = p.parameters.map(pm => ({
          type: pm.type || 'button', label: pm.label || '', path: pm.path || '', show: pm.show || ''
        }));
      }
    },

    addPayloadField() {
      this.payloadFields.push({ _uid: this.newUid(), key: '', type: 'data', dataField: '', esbAlias: '', esbProp: '', resultField: '', literalValue: '', filter: '' });
    },
    removePayloadField(i) { this.payloadFields.splice(i, 1); },
    addParameter()        { this.parameters.push({ type: 'button', label: '', path: '', show: '' }); },
    removeParameter(i)    { this.parameters.splice(i, 1); },

    copyJson() {
      navigator.clipboard.writeText(this.jsonPreview).catch(() => {});
    }
  }
};
</script>

<style scoped>
/* ── Root ───────────────────────────────── */
.dre-root { font-family: 'Inter', 'Roboto', sans-serif; }

/* ── Section layout ─────────────────────── */
.dre-section { padding: 2px 0; }

.dre-section-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}
.dre-section-icon {
  width: 28px; height: 28px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.dre-section-icon.indigo  { background: #3f51b5; }
.dre-section-icon.teal    { background: #00897b; }
.dre-section-icon.orange  { background: #f57c00; }
.dre-section-icon.green   { background: #388e3c; }
.dre-section-icon.grey    { background: #616161; }

.dre-section-title { font-weight: 700; font-size: 13px; color: #222; line-height: 1.3; }
.dre-section-sub   { font-size: 11px; color: #888; margin-top: 1px; }
.dre-divider       { border-top: 1px solid #ebebeb; margin: 14px 0; }

/* ── Field card ────────────────────────── */
.dre-field-card {
  border: 1px solid #e8e8f0;
  border-radius: 8px;
  background: #fafafe;
  margin-bottom: 8px;
  overflow: hidden;
}
.dre-field-header {
  display: flex; align-items: center; flex-wrap: wrap;
  gap: 6px; padding: 8px 10px 6px 10px;
  background: #f3f4f8;
  border-bottom: 1px solid #e8e8f0;
}
.dre-field-body {
  padding: 10px 10px 8px 10px;
  display: flex; flex-direction: column; gap: 8px;
}

/* ── Key combobox ──────────────────────── */
.dre-key-box { max-width: 130px; font-size: 13px; }
.dre-key-box >>> .v-input__control { background: transparent !important; }
.dre-key-box >>> .v-input__slot { box-shadow: none !important; border: 1px solid #d0d0e0 !important; border-radius: 5px; background: #fff !important; }

/* ── Type chips ────────────────────────── */
.dre-type-chips { display: flex; flex-wrap: wrap; gap: 4px; }
.dre-type-chip  { cursor: pointer; font-size: 11px !important; font-weight: 600; transition: all 0.15s; }
.dre-type-chip:hover { opacity: 0.85; }

/* ── ESB row ───────────────────────────── */
.dre-esb-row   { display: flex; align-items: center; gap: 4px; }
.dre-esb-alias { flex: 1; }
.dre-esb-prop  { flex: 0 0 150px; }
.dre-dot-sep   { font-weight: 700; color: #888; padding: 0 2px; font-size: 16px; }
.dre-value-input { width: 100%; }

/* ── Filter chips ──────────────────────── */
.dre-filter-row  { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }
.dre-filter-label { font-size: 11px; color: #999; white-space: nowrap; }
.dre-filter-chip { cursor: pointer; font-size: 10.5px !important; transition: all 0.15s; }
.dre-filter-chip:hover { opacity: 0.8; }

/* ── Expression preview ────────────────── */
.dre-expr-preview {
  display: flex; align-items: center;
  font-size: 11px; color: #777;
}
.expr-code {
  background: #f0f0f8; border-radius: 4px;
  padding: 1px 6px; font-size: 11px; color: #3f51b5;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
}

/* ── URI tokens ────────────────────────── */
.dre-token-row { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }

/* ── Topic chips ───────────────────────── */
.dre-topic-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.dre-topic-chip  { cursor: pointer; font-size: 12px !important; font-weight: 600; transition: all 0.15s; }
.dre-topic-chip:hover { opacity: 0.85; }

.dre-topic-hint {
  font-size: 11px; border-radius: 6px;
  padding: 5px 10px;
  display: flex; align-items: center;
}
.hint-public { background: #fff3e0; color: #e65100; }
.hint-user   { background: #e8eaf6; color: #283593; }
.hint-group  { background: #e8f5e9; color: #1b5e20; }
.hint-artist { background: #f3e5f5; color: #4a148c; }

/* ── Target value card ─────────────────── */
.dre-target-value-card {
  border: 1px solid #e0e0ea; border-radius: 8px;
  padding: 10px; background: #fafafe;
}

/* ── Param card ────────────────────────── */
.dre-param-card {
  border: 1px solid #e8e8f0; border-radius: 8px;
  background: #fafafe; overflow: hidden;
}
.dre-param-header {
  display: flex; align-items: center;
  padding: 7px 10px; background: #f3f4f8;
  border-bottom: 1px solid #e8e8f0;
}
.dre-param-label { font-size: 12px; font-weight: 600; color: #444; }
.dre-param-body  { padding: 10px; }

/* ── JSON preview ──────────────────────── */
.dre-json-wrap {
  background: #1a1b2e; border-radius: 8px;
  padding: 12px 14px; max-height: 220px; overflow-y: auto;
}
.dre-json {
  color: #a9b1d6; font-size: 11px;
  font-family: 'JetBrains Mono', 'Fira Code', 'Courier New', monospace;
  white-space: pre-wrap; word-break: break-all; margin: 0;
}

/* ── Optional badge ────────────────────── */
.optional-badge {
  font-size: 10px; font-weight: 500;
  background: #e8f5e9; color: #388e3c;
  border-radius: 10px; padding: 1px 7px;
  margin-left: 6px; vertical-align: middle;
}

/* ── Fields list ───────────────────────── */
.dre-fields-list { display: flex; flex-direction: column; }

/* ── Transitions ───────────────────────── */
.field-fade-enter-active, .field-fade-leave-active { transition: all 0.2s; }
.field-fade-enter, .field-fade-leave-to { opacity: 0; transform: translateY(-6px); }

code {
  background: #f0f0f8; border-radius: 3px;
  padding: 0 4px; font-size: 11px; color: #5c6bc0;
  font-family: 'JetBrains Mono', monospace;
}
</style>
